// Spot the Fraud: round data and pure game rules. No React, no DOM, so the
// node tests can bundle this file on its own.
//
// Every round is one verified original document plus exactly one change made
// by the attacker. The manipulated version is always derived from the
// original, so the two sides can never drift apart.

export type RoundType = "email" | "invoice" | "attachment" | "shareddoc" | "website";

export type FieldKind = "text" | "title" | "mono" | "muted" | "body" | "file" | "button" | "link" | "url";

export interface FieldDef {
  key: string;
  /** Plain name shown in the selection readout and the reveal. */
  label: string;
  kind?: FieldKind;
  /** Visible text for link fields; the field value is the real target. */
  text?: string;
  /** Not shown until the learner presses INSPECT (like a real mail app). */
  hidden?: boolean;
}

export interface SectionDef {
  id: string;
  label: string;
  fields: FieldDef[];
}

export interface RoundDef {
  id: string;
  type: RoundType;
  tab: string;
  /** One line telling the learner what to compare. */
  instruction: string;
  /** Short name of what the attacker changed, for the recap. */
  changeLabel: string;
  /** Topic + lesson feed the course debrief when the round is missed. */
  topic: string;
  lesson: string;
  sections: SectionDef[];
  original: Readonly<Record<string, string>>;
  mutation: { field: string; value: string };
  /** Section the hint frames (never the exact field). */
  hintSection: string;
}

export const ROUNDS: readonly RoundDef[] = [
  {
    id: "sf-email-sender",
    type: "email",
    tab: "EMAIL",
    instruction: "Compare the two emails. Press INSPECT to show hidden details, click the detail the attacker changed, then press SUBMIT.",
    changeLabel: "Sender domain",
    topic: "Lookalike sender domain",
    lesson: "Read the sender address letter by letter: a lookalike domain swaps a single character, such as l for i.",
    sections: [
      {
        id: "sender",
        label: "Sender",
        fields: [
          { key: "from_name", label: "Sender name", kind: "title" },
          { key: "from_addr", label: "Sender address", kind: "muted", hidden: true },
        ],
      },
      {
        id: "details",
        label: "Details",
        fields: [
          { key: "to", label: "Recipient", kind: "muted" },
          { key: "date", label: "Date", kind: "muted" },
          { key: "subject", label: "Subject", kind: "title" },
        ],
      },
      {
        id: "message",
        label: "Message",
        fields: [
          { key: "body", label: "Message text", kind: "body" },
          { key: "signature", label: "Signature", kind: "muted" },
        ],
      },
    ],
    original: {
      from_name: "Mara Velden, Northstar Logistics",
      from_addr: "finance@northstar-logistics.com",
      to: "To: accounts@harbourline-retail.com",
      date: "Tue 11 Nov 2026, 09:14",
      subject: "Invoice INV-2026-1047, October freight",
      body: "Hello Accounts team, please find our invoice for October freight attached. Payment is due by 14 Nov 2026.",
      signature: "Mara Velden, Accounts Receivable",
    },
    mutation: { field: "from_addr", value: "finance@northstar-logistlcs.com" },
    hintSection: "sender",
  },
  {
    id: "sf-invoice-bank",
    type: "invoice",
    tab: "INVOICE",
    instruction: "Compare the two invoices. Press INSPECT to show hidden details, click the detail the attacker changed, then press SUBMIT.",
    changeLabel: "Bank account",
    topic: "Bank detail change",
    lesson: "A changed bank account on a familiar invoice is classic payment fraud: confirm it by phone on a number you already have.",
    sections: [
      {
        id: "supplier",
        label: "Supplier",
        fields: [
          { key: "sup_name", label: "Supplier", kind: "title" },
          { key: "sup_addr", label: "Supplier address", kind: "muted" },
          { key: "sup_vat", label: "VAT ID", kind: "muted" },
        ],
      },
      {
        id: "invoice",
        label: "Invoice",
        fields: [
          { key: "inv_no", label: "Invoice number", kind: "text" },
          { key: "inv_date", label: "Invoice date", kind: "text" },
          { key: "inv_due", label: "Due date", kind: "text" },
        ],
      },
      {
        id: "items",
        label: "Items",
        fields: [
          { key: "item", label: "Line item", kind: "text" },
          { key: "amount", label: "Total amount", kind: "title" },
        ],
      },
      {
        id: "payment",
        label: "Payment details",
        fields: [
          { key: "bank", label: "Bank", kind: "text" },
          { key: "holder", label: "Account holder", kind: "text" },
          { key: "iban", label: "Bank account (IBAN)", kind: "mono" },
        ],
      },
    ],
    original: {
      sup_name: "Northstar Logistics GmbH",
      sup_addr: "Hafenstrasse 18, 20457 Hamburg",
      sup_vat: "VAT ID DE 318 554 207",
      inv_no: "INV-2026-1047",
      inv_date: "31 Oct 2026",
      inv_due: "14 Nov 2026",
      item: "Freight services, October 2026",
      amount: "EUR 24,850.00",
      bank: "Elbmark Bank AG",
      holder: "Northstar Logistics GmbH",
      iban: "DE21 2004 1155 0912 0048 21",
    },
    mutation: { field: "iban", value: "DE21 2004 1155 0912 0072 84" },
    hintSection: "payment",
  },
  {
    id: "sf-attachment-ext",
    type: "attachment",
    tab: "ATTACHMENT",
    instruction: "Compare the two attachments. Press INSPECT to show hidden details, click the detail the attacker changed, then press SUBMIT.",
    changeLabel: "File name",
    topic: "Disguised file type",
    lesson: "Check the whole file name: a second extension such as .pdf.exe means the file is a program, not a document.",
    sections: [
      {
        id: "message",
        label: "Message",
        fields: [
          { key: "att_from", label: "Sender address", kind: "muted" },
          { key: "att_subject", label: "Subject", kind: "title" },
          { key: "att_body", label: "Message text", kind: "body" },
        ],
      },
      {
        id: "file",
        label: "Attachment",
        fields: [
          { key: "att_name", label: "File name", kind: "file" },
          { key: "att_size", label: "File size", kind: "muted" },
        ],
      },
    ],
    original: {
      att_from: "finance@northstar-logistics.com",
      att_subject: "Invoice INV-2026-1047",
      att_body: "Invoice attached as discussed on the phone. Thank you for your business.",
      att_name: "Invoice_INV-2026-1047.pdf",
      att_size: "184 KB",
    },
    mutation: { field: "att_name", value: "Invoice_INV-2026-1047.pdf.exe" },
    hintSection: "file",
  },
  {
    id: "sf-shared-link",
    type: "shareddoc",
    tab: "SHARED DOCUMENT",
    instruction: "Compare the two share notices. Press INSPECT to show hidden details, click the detail the attacker changed, then press SUBMIT.",
    changeLabel: "Sign-in link host",
    topic: "Lookalike sign-in link",
    lesson: "Read the host right before the first single slash: docs-northstar-logistics.com is a different site from docs.northstar-logistics.com.",
    sections: [
      {
        id: "sharer",
        label: "Shared by",
        fields: [
          { key: "sh_name", label: "Shared by", kind: "title" },
          { key: "sh_mail", label: "Sharer address", kind: "muted" },
          { key: "sh_date", label: "Date", kind: "muted" },
        ],
      },
      {
        id: "document",
        label: "Document",
        fields: [
          { key: "sh_doc", label: "Document name", kind: "file" },
          { key: "sh_msg", label: "Message text", kind: "body" },
        ],
      },
      {
        id: "link",
        label: "Sign-in link",
        fields: [
          { key: "sh_button", label: "Button text", kind: "button" },
          { key: "sh_url", label: "Sign-in link", kind: "url", hidden: true },
        ],
      },
    ],
    original: {
      sh_name: "Mara Velden",
      sh_mail: "mara.velden@northstar-logistics.com",
      sh_date: "Tue 11 Nov 2026, 09:22",
      sh_doc: "Q4 Freight Rates 2026.xlsx",
      sh_msg: "Updated Q4 rates, please review before Friday.",
      sh_button: "Open document",
      sh_url: "https://docs.northstar-logistics.com/s/q4-rates",
    },
    mutation: { field: "sh_url", value: "https://docs-northstar-logistics.com/s/q4-rates" },
    hintSection: "link",
  },
  {
    id: "sf-website-link",
    type: "website",
    tab: "WEBSITE",
    instruction: "Compare the two pages. Hover over links or press INSPECT to see where they really go, click the changed detail, then SUBMIT.",
    changeLabel: "Link target",
    topic: "Hidden link target",
    lesson: "Link text can say anything: hover and read the real address in the status bar before you click.",
    sections: [
      {
        id: "address",
        label: "Address bar",
        fields: [{ key: "web_url", label: "Page address", kind: "url" }],
      },
      {
        id: "page",
        label: "Page content",
        fields: [
          { key: "web_head", label: "Heading", kind: "title" },
          { key: "web_text", label: "Page text", kind: "body" },
          { key: "web_link", label: "Sign-in link target", kind: "link", text: "Sign in to your account" },
        ],
      },
      {
        id: "footer",
        label: "Footer",
        fields: [
          { key: "web_phone", label: "Support number", kind: "muted" },
          { key: "web_copy", label: "Copyright line", kind: "muted" },
        ],
      },
    ],
    original: {
      web_url: "https://portal.northstar-logistics.com/billing",
      web_head: "Update your billing details",
      web_text: "Your saved payment method expires this month. Sign in to keep your deliveries running.",
      web_link: "https://portal.northstar-logistics.com/login",
      web_phone: "Support +49 40 555 0182",
      web_copy: "\u00a9 2026 Northstar Logistics GmbH",
    },
    mutation: { field: "web_link", value: "https://northstar-portal-login.com/auth" },
    hintSection: "page",
  },
];

// ------------------------------------------------------------------ helpers

export function fieldKeys(round: RoundDef): string[] {
  return round.sections.flatMap((s) => s.fields.map((f) => f.key));
}

export function fieldDef(round: RoundDef, key: string): FieldDef | undefined {
  for (const s of round.sections) for (const f of s.fields) if (f.key === key) return f;
  return undefined;
}

export function sectionOf(round: RoundDef, key: string): string | undefined {
  return round.sections.find((s) => s.fields.some((f) => f.key === key))?.id;
}

/** Copy of the original with the attacker's single change applied. */
export function manipulate(round: RoundDef): Record<string, string> {
  const { field, value } = round.mutation;
  if (!(field in round.original)) throw new Error(`spotfraud: mutation field "${field}" is not in round ${round.id}`);
  if (round.original[field] === value) throw new Error(`spotfraud: mutation in round ${round.id} changes nothing`);
  return { ...round.original, [field]: value };
}

/** Keys whose values differ between two documents (union of both key sets). */
export function changedKeys(a: Readonly<Record<string, string>>, b: Readonly<Record<string, string>>): string[] {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].filter((k) => a[k] !== b[k]);
}

/** Split two strings into shared prefix, differing middles and shared suffix. */
export function diffParts(a: string, b: string): { prefix: string; a: string; b: string; suffix: string } {
  let p = 0;
  while (p < a.length && p < b.length && a[p] === b[p]) p++;
  let s = 0;
  while (s < a.length - p && s < b.length - p && a[a.length - 1 - s] === b[b.length - 1 - s]) s++;
  return {
    prefix: a.slice(0, p),
    a: a.slice(p, a.length - s),
    b: b.slice(p, b.length - s),
    suffix: a.slice(a.length - s),
  };
}

// ------------------------------------------------------------------ scoring

export const SCORE = {
  CORRECT: 100,
  NO_HINT_BONUS: 50,
  WRONG: -10,
  HINT: -20,
  SKIP: 0,
} as const;

/** Share of rounds needed to clear the cabinet (mirrors PASS_RATIO.spotfraud). */
export const SF_PASS_RATIO = 0.8;

export type Outcome = "found" | "wrong" | "skipped";

export function judge(round: RoundDef, pickedKey: string | null): Outcome {
  if (pickedKey === null) return "skipped";
  return pickedKey === round.mutation.field ? "found" : "wrong";
}

/** Points for the answer itself (the hint cost is charged when the hint is used). */
export function outcomePoints(outcome: Outcome, hintUsed: boolean): number {
  if (outcome === "found") return SCORE.CORRECT + (hintUsed ? 0 : SCORE.NO_HINT_BONUS);
  if (outcome === "wrong") return SCORE.WRONG;
  return SCORE.SKIP;
}

/** Net points of a round, hint cost included. */
export function roundPoints(outcome: Outcome, hintUsed: boolean): number {
  return outcomePoints(outcome, hintUsed) + (hintUsed ? SCORE.HINT : 0);
}

/** Running total never drops below zero. */
export function addPoints(total: number, delta: number): number {
  return Math.max(0, total + delta);
}

export interface RoundRecord {
  roundId: string;
  outcome: Outcome;
  hintUsed: boolean;
  picked: string | null;
  /** Net points of the round (hint included). */
  points: number;
  ms: number;
}

/** Replays a run in game order: hint cost first, then the answer, floored at 0 after each step. */
export function runTotal(records: readonly Pick<RoundRecord, "outcome" | "hintUsed">[]): number {
  let t = 0;
  for (const r of records) {
    if (r.hintUsed) t = addPoints(t, SCORE.HINT);
    t = addPoints(t, outcomePoints(r.outcome, r.hintUsed));
  }
  return t;
}

export function passes(correct: number, total: number): boolean {
  return total > 0 && correct / total + 1e-9 >= SF_PASS_RATIO;
}

export interface ResultInput {
  rawScore: number;
  correct: number;
  incorrect: number;
  total: number;
  mistakes: { topic: string; lesson: string }[];
}

/** Input for makeResult("spotfraud", ...). Wrong and skipped rounds both count as missed. */
export function buildResultInput(
  records: readonly RoundRecord[],
  rawScore: number,
  rounds: readonly RoundDef[] = ROUNDS,
): ResultInput {
  const byId = new Map(rounds.map((r) => [r.id, r]));
  const correct = records.filter((r) => r.outcome === "found").length;
  const mistakes = records
    .filter((r) => r.outcome !== "found")
    .map((r) => byId.get(r.roundId))
    .filter((r): r is RoundDef => !!r)
    .map((r) => ({ topic: r.topic, lesson: r.lesson }));
  return {
    rawScore: Math.max(0, Math.round(rawScore)),
    correct,
    incorrect: records.length - correct,
    total: rounds.length,
    mistakes,
  };
}
